import React from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import {
  ClipboardList,
  Bolt,
  ShieldCheck
} from 'lucide-react';

import Pic1 from "../images/At1.jpg";
import Pic2 from "../images/At2.jpg";
import Pic3 from "../images/At3.jpg";
import Pic4 from "../images/At4.jpg";
import backgroundImg from "../images/At5.jpg";
import TopImage from '../images/Hero1.jpg';

const showcaseImages = [
  {
    src: Pic1,
     alt: 'Locomotive Control Panel',
    description: 'State-of-the-art control panels engineered for high-speed communication, fault diagnosis, and real-time monitoring.',
  },
  {
    src: Pic2,
    alt: 'High-Torque Traction Motor',
    description: 'Robust traction motor delivering consistent power and performance under varying track conditions.',
},
  {
    src:Pic3,
    alt: 'Pantograph Overhead System',
    description: 'Precision current collection system ensuring reliable energy transmission from overhead catenaries.',
  },
  {
    src: Pic4,
    alt: 'Auxiliary Power Supply System',
    description: 'Integrated auxiliary systems providing power for onboard services including HVAC, lighting, and control electronics.',
   },
];

function Switchyard() {
  return (
    <>
     <Navbar />

      {/* Hero Section */}
      <div className="relative pt-4">
        <img
          src={TopImage}
          alt="Electric locomotive on railway track"
          className="w-full max-h-[500px] object-cover object-center"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white text-center px-4">
            Locomotive 
          </h2>
        </div>
      </div>

      {/* Introduction */}
      <section className="bg-blue-gray-300 py-20 px-6 sm:px-10 lg:px-16 overflow-hidden relative">
        <div className="absolute inset-0">
          <img
            src={backgroundImg}
            alt="Locomotive background schematic"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto text-center md:text-left">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-6">
            Engineering the Future of Rail Mobility
          </h1>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At iNVORA, we specialize in advanced locomotive electrical systems that power next-generation rail transport. From propulsion and regenerative braking to onboard energy storage and control electronics — we engineer every component with precision and purpose.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Our team brings together expertise in rolling stock design, traction systems, pantograph interfacing, and auxiliary power distribution, ensuring your locomotive is optimized for safety, performance, and energy efficiency.
          </p>
        </div>
      </section>

      {/* Key Features */}
      <section className="bg-white py-20 px-6 sm:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-16">
            Our Locomotive Engineering Expertise
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-left">
            <div className="bg-gray-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition duration-300">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-100 text-green-600 mb-6 mx-auto md:mx-0">
                <ClipboardList className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">System-Level Design</h3>
              <p className="text-gray-700 text-base">
                From traction and braking circuits to inverter sizing and earthing layouts — we develop end-to-end locomotive power systems.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition duration-300">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-yellow-100 text-yellow-600 mb-6 mx-auto md:mx-0">
                <Bolt className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Traction & Energy Efficiency</h3>
              <p className="text-gray-700 text-base">
                We optimize energy flow from overhead lines or onboard batteries to traction motors, reducing losses and maximizing regeneration.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition duration-300">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-600 mb-6 mx-auto md:mx-0">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Protection & Control</h3>
              <p className="text-gray-700 text-base">
                Intelligent control systems, fault detection algorithms, and thermal protection ensure reliability across long hauls and adverse climates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="bg-gray-50 py-20 px-6 sm:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">Locomotive Designs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {showcaseImages.map(({ src, alt, description }, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl shadow-lg bg-white p-3 hover:scale-105 transition transform duration-300"
              >
                <img
                  src={src}
                  alt={alt}
                  className="w-full h-52 object-cover rounded-lg mb-4"
                />
                <p className="text-sm text-gray-800 font-semibold">{alt}</p>
                <p className="text-xs text-gray-500 mt-1">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-blue-700 py-16 text-white text-center px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-4xl font-extrabold mb-6">Need a Locomotive Design Partner?</h3>
          <p className="mb-8 text-lg max-w-xl mx-auto">
            Connect with our railway systems engineers for expert guidance on locomotive traction, control, and power conversion systems.
          </p>
          <a
            href="/#contact"
            className="inline-block bg-white text-blue-700 font-semibold px-8 py-4 rounded-lg shadow-lg hover:bg-gray-100 transition"
          >
            Get In Touch
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Switchyard;